// SuperProtectedRoute — gates the /super/* subtree (Wave I).
//
// Distinct from `ProtectedRoute` (which gates tenant-staff routes). This
// guard reads `useSuperAdminStore` and redirects to `/super/login` when
// no super-admin session is active. The two stores are deliberately
// separate — a tenant staff session never grants access to /super, and
// vice versa.
//
// On boot, the store starts in 'loading'. We hydrate it from storage: if
// the super-admin access token slot (`superAccessToken`) holds a value,
// we treat the session as authenticated. Subsequent API calls validate
// the token; a 401 will trigger logout via the axios interceptor.

import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useSuperAdminStore } from '../store/superAdminStore';
import { storage } from '../utils/storage';

interface SuperProtectedRouteProps {
  children?: ReactNode;
}

export function SuperProtectedRoute({ children }: SuperProtectedRouteProps) {
  const status = useSuperAdminStore((s) => s.status);
  const setAuthenticated = useSuperAdminStore((s) => s.setAuthenticated);
  const setAnonymous = useSuperAdminStore((s) => s.setAnonymous);
  const location = useLocation();

  useEffect(() => {
    if (status !== 'loading') return;
    const superAccessToken = storage.get('superAccessToken');
    if (typeof superAccessToken === 'string' && superAccessToken.length > 0) {
      // We don't have admin name/email until the next API call returns it
      // (or the user logs in fresh). Hydrate with a minimal placeholder so
      // the gate opens; pages that need the admin summary should refetch.
      setAuthenticated({ name: '', email: '' });
    } else {
      setAnonymous();
    }
  }, [status, setAuthenticated, setAnonymous]);

  if (status === 'loading') {
    return <SuperLoading />;
  }

  if (status === 'anonymous') {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/super/login?redirect=${redirect}`} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}

function SuperLoading() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-page-bg, #f8fafc)',
        color: 'var(--color-text-secondary, #475569)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      Loading…
    </div>
  );
}
